import React, { useState } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import './MobileDemoGate.css';

/**
 * On mobile, defers rendering an animated demo until the user taps play.
 * Reserves a fixed height placeholder so the page does not jump as demos
 * mount/animate. On desktop, renders the demo immediately with default
 * (looping autoplay) behavior.
 *
 * Usage:
 *   <MobileDemoGate label="portfolio demo">
 *     {({ autoPlay, loop }) => <PortfolioDemo autoPlay={autoPlay} loop={loop} />}
 *   </MobileDemoGate>
 *
 * On mobile: initially shows play button. After tap, renders child with
 * autoPlay=true, loop=false — demo plays once and holds its final frame.
 * Tapping again replays.
 */
export default function MobileDemoGate({ children, label = 'demo', placeholderHeight = 360 }) {
  const isMobile = useIsMobile();
  const [playToken, setPlayToken] = useState(0); // increments to force remount on replay
  const [hasPlayed, setHasPlayed] = useState(false);

  if (!isMobile) {
    // Desktop: original behavior — autoplay + loop
    return children({ autoPlay: true, loop: true });
  }

  if (!hasPlayed) {
    return (
      <button
        type="button"
        className="mobile-demo-gate"
        style={{ minHeight: placeholderHeight }}
        onClick={() => { setHasPlayed(true); setPlayToken(t => t + 1); }}
        aria-label={`Play ${label}`}
      >
        <span className="mobile-demo-gate__titlebar">
          <span className="mobile-demo-gate__dot red" />
          <span className="mobile-demo-gate__dot yellow" />
          <span className="mobile-demo-gate__dot green" />
          <span className="mobile-demo-gate__titlebar-text">alphagrade.io</span>
        </span>
        <span className="mobile-demo-gate__play">
          <svg viewBox="0 0 24 24" width="36" height="36" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
        <span className="mobile-demo-gate__label">Tap to play {label}</span>
      </button>
    );
  }

  // Mobile, after tap: render the demo once, no loop. Remount on replay.
  return (
    <div className="mobile-demo-gate__active">
      <div key={playToken}>
        {children({ autoPlay: true, loop: false })}
      </div>
      <button
        type="button"
        className="mobile-demo-gate__replay"
        onClick={() => setPlayToken(t => t + 1)}
      >
        Replay
      </button>
    </div>
  );
}
